import { createContext, useContext } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { empresasService } from "app/services";
import { ErrorMessage, Spinner } from "app/components";

const EstablecimientosContext = createContext();

export function EstablecimientosProvider({ children }) {
  const params = useParams();
  const navigate = useNavigate();

  const establecimientosQuery = useQuery({
    queryKey: ["empresa:get:establecimientos", params.empresa_id],
    queryFn: () =>
      empresasService.getEstablecimientos({ empresaId: params.empresa_id }),
  });

  const establecimientoAddMutation = useMutation({
    queryKey: ["empresa:add:establecimiento", params.empresa_id],
    mutationFn: (data) =>
      toast.promise(empresasService.addEstablecimiento(data), {
        loading: "Agregando el establecimiento...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await establecimientosQuery.refetch();
    },
  });

  const establecimientoUpdateMutation = useMutation({
    queryKey: ["empresa:update:establecimiento"],
    mutationFn: (data) =>
      toast.promise(empresasService.updateEstablecimiento(data), {
        loading: "Actualizando el establecimiento...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await establecimientosQuery.refetch();
    },
  });

  const establecimientoDeleteMutation = useMutation({
    queryKey: ["empresa:delete:establecimiento"],
    mutationFn: (data) =>
      toast.promise(empresasService.deleteEstablecimiento(data), {
        loading: "Eliminando el establecimiento...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await establecimientosQuery.refetch();
    },
  });

  const establecimientoVerifyMutation = useMutation({
    mutationKey: ["empresa:verify:establecimiento"],
    mutationFn: (data) =>
      toast.promise(empresasService.verifyEstablecimiento(data), {
        loading: "Verificando el establecimiento...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await establecimientosQuery.refetch();
    },
  });

  async function addEstablecimiento({ establecimiento }) {
    return await establecimientoAddMutation.mutateAsync({
      empresaId: params.empresa_id,
      establecimiento,
    });
  }

  async function updateEstablecimiento({ establecimientoId, establecimiento }) {
    const { establecimiento_verificado, ...rest } = establecimiento;

    return await establecimientoUpdateMutation.mutateAsync({
      empresaId: params.empresa_id,
      establecimientoId,
      establecimiento: rest,
    });
  }

  async function deleteEstablecimiento({ establecimientoId }) {
    const deleted = window.confirm("¿Desea eliminar este establecimiento?");

    if (!!deleted) {
      await establecimientoDeleteMutation.mutateAsync({
        empresaId: params.empresa_id,
        establecimientoId: establecimientoId,
      });

      return true;
    }

    return false;
  }

  async function verifyEstablecimiento({ establecimientoId }) {
    const confirmed = window.confirm("¿Desea verificar este establecimiento?");

    if (confirmed) {
      return await establecimientoVerifyMutation.mutateAsync({
        empresaId: params.empresa_id,
        establecimientoId,
      });
    }

    return false;
  }

  function cancel() {
    if (window.confirm("¿Desea cancelar la edición?")) {
      return navigate(".");
    }
  }

  if (!!establecimientosQuery.isLoading) {
    return <Spinner label="Cargando los establecimientos..." />;
  } else if (!!establecimientosQuery.isError) {
    return (
      <ErrorMessage message={establecimientosQuery.error.response.statusText} />
    );
  }

  return (
    <EstablecimientosContext.Provider
      value={{
        establecimientos: establecimientosQuery.data,
        isLoading: establecimientosQuery.isLoading,
        isFetching: establecimientosQuery.isFetching,
        isRefetching: establecimientosQuery.isRefetching,
        addEstablecimiento,
        isAdding: establecimientoAddMutation.isLoading,
        updateEstablecimiento,
        isUpdating: establecimientoUpdateMutation.isLoading,
        deleteEstablecimiento,
        isDeleting: establecimientoDeleteMutation.isLoading,
        verifyEstablecimiento,
        isVerifing: establecimientoVerifyMutation.isLoading,
        cancel,
      }}
    >
      {children}
    </EstablecimientosContext.Provider>
  );
}

export function useEstablecimientosContext() {
  const establecimientosContext = useContext(EstablecimientosContext);

  if (!establecimientosContext) {
    throw new Error(
      "useEstablecimientosContext should be used inside the EstablecimientosProvider."
    );
  }

  return establecimientosContext;
}
